// ---------------------------------------------------------------------------
// Highway-comparison badge — tunable thresholds.
// ---------------------------------------------------------------------------
// Single source of truth for the "X% less / more highway than Google Maps"
// pill. Both values are pulled from Vite env vars when present so they can
// be tuned per-environment (preview, staging, prod) without touching any
// component. Anything else in the codebase MUST import from here rather
// than redefining its own copy — the gate, the wording, the popover legend
// and the on-card legend all share these numbers and would mislead users
// the moment they drifted apart.
//
// Env vars (optional, all VITE_ prefixed so Vite exposes them client-side):
//   VITE_HIGHWAY_BADGE_MIN_PP  — noise floor in percentage points (default 5)
//   VITE_HIGHWAY_BADGE_CAP_PP  — display cap in percentage points (default 95)
// ---------------------------------------------------------------------------

const DEFAULT_MIN_PP = 5;
const DEFAULT_CAP_PP = 95;

/** Parse an env value into a finite, in-range number, falling back when the
 *  value is missing, malformed, or outside [0, 100]. Tightened bounds keep a
 *  typo (e.g. "500") from silently nuking the badge for everyone. */
const parsePp = (raw: unknown, fallback: number): number => {
  if (raw == null || raw === "") return fallback;
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n) || n < 0 || n > 100) return fallback;
  return n;
};

const env = (typeof import.meta !== "undefined" ? import.meta.env : undefined) as
  | Record<string, string | undefined>
  | undefined;

/** Minimum gap (percentage points, after rounding) before the badge appears.
 *  Below this the difference is statistical noise and the badge is hidden. */
export const HIGHWAY_BADGE_MIN_PP: number = parsePp(
  env?.VITE_HIGHWAY_BADGE_MIN_PP,
  DEFAULT_MIN_PP,
);

/** Display cap (percentage points). Massive gaps render as "X% (capped)" so
 *  we never claim "100% less highway" — an unverifiable absolute. */
export const HIGHWAY_BADGE_CAP_PP: number = parsePp(
  env?.VITE_HIGHWAY_BADGE_CAP_PP,
  DEFAULT_CAP_PP,
);

/** Bundled config object — handy when callers want to pass the whole tuning
 *  surface around (e.g. analytics, debug overlays) without importing each
 *  constant individually. */
export const HIGHWAY_BADGE_CONFIG = {
  minPp: HIGHWAY_BADGE_MIN_PP,
  capPp: HIGHWAY_BADGE_CAP_PP,
} as const;
